import { useEffect, useRef, useState } from 'react';
import { Icon } from '../components/UI';
import { Link, navigate } from '../lib/router';
import { api } from '../lib/api';

const navGroups = [
  {
    label: 'Operate',
    items: [
      ['/merchant', 'home', 'Overview'],
      ['/merchant/redeem', 'scan', 'Redeem'],
      ['/merchant/voucher', 'ticket', 'Vouchers'],
      ['/merchant/customers', 'people', 'Customers'],
      ['/merchant/qr', 'qr', 'Counter QR'],
    ],
  },
  {
    label: 'Manage',
    items: [
      ['/merchant/settings', 'settings', 'Business Settings'],
      ['/merchant/billing', 'billing', 'Plan & Billing'],
      ['/merchant/contact', 'people', 'Contact Us'],
      ['/merchant/staff', 'people', 'Staff Access', 'locked'],
    ],
  },
  {
    label: 'Grow',
    items: [
      ['/merchant/analytics', 'chart', 'Analytics', 'locked'],
      ['/merchant/relations', 'network', 'Merchant Network', 'locked'],
    ],
  },
];

export default function DashboardLayout({ user, path, children, onLogout }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const business = user.merchant || user.business || {};
  const businessName = business.name || 'Your business';
  const outletName = business.outletName || 'Main outlet';
  const initial = businessName.trim().charAt(0).toUpperCase() || 'L';
  const desktopRecommended = ['/merchant/voucher', '/merchant/qr', '/merchant/settings'].includes(path);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [path]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setMobileNavOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [mobileNavOpen]);

  async function logout() {
    await api.logout().catch(() => {});
    onLogout();
    navigate('/merchant/login');
  }

  return (
    <div className="app-shell">
      <aside id="merchant-navigation" className={`sidebar ${mobileNavOpen ? 'is-open' : ''}`}>
        <div className="sidebar-heading">
          <Link className="sidebar-brand" to="/merchant" onClick={() => setMobileNavOpen(false)}>
            <img
              className="dashboard-logo sidebar-logo"
              src="/logo-v2-wordmark-dark.png"
              alt="LoyalQR.sg"
            />
          </Link>
          <button
            ref={closeButtonRef}
            className="mobile-drawer-close"
            type="button"
            aria-label="Close navigation"
            onClick={() => {
              setMobileNavOpen(false);
              menuButtonRef.current?.focus();
            }}
          >
            <Icon name="close" />
          </button>
        </div>
        <nav aria-label="Merchant navigation">
          {navGroups.map((group) => (
            <section key={group.label} className={`nav-group nav-group-${group.label.toLowerCase()}`}>
              <span className="nav-group-label">{group.label}</span>
              {group.items.map(([to, icon, label, state]) => (
                <Link key={to} to={to} className={path === to ? 'active' : ''} onClick={() => setMobileNavOpen(false)}>
                  <Icon name={icon} />
                  <span>{label}</span>
                  {state === 'locked' && <span className="nav-lock">Locked</span>}
                  {state === 'soon' && <span className="nav-lock">Soon</span>}
                </Link>
              ))}
            </section>
          ))}
        </nav>
        <div className="mobile-drawer-account">
          <span>{user.email}</span>
          <button type="button" onClick={logout}>Log out</button>
        </div>
      </aside>

      {mobileNavOpen && (
        <button
          className="mobile-nav-overlay"
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <header className="business-bar">
        <button
          ref={menuButtonRef}
          className="mobile-menu-button"
          type="button"
          aria-label="Open navigation"
          aria-controls="merchant-navigation"
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen(true)}
        >
          <Icon name="menu" />
        </button>
        <Link className="mobile-brand" to="/merchant">
          <img className="dashboard-logo" src="/logo-v2-wordmark.png" alt="LoyalQR.sg" />
        </Link>
        <div className="business-identity">
          <span className={`business-avatar ${business.logoUrl ? 'has-photo' : ''}`}>
            {business.logoUrl ? <img src={business.logoUrl} alt="" /> : initial}
          </span>
          <div>
            <strong>{businessName}</strong>
            <small>{outletName}</small>
          </div>
        </div>
        {user.isTrial && <span className="topbar-trial-status">Trial Mode</span>}
        <div className="business-account">
          <span>{user.email}</span>
          <button onClick={logout}>Log out</button>
        </div>
      </header>

      <main className="merchant-main">
        {desktopRecommended && (
          <aside className="desktop-experience-note" role="note">
            <Icon name="desktop" />
            <div>
              <strong>Desktop recommended for editing</strong>
              <span>This setup page is easier to review on a larger screen.</span>
            </div>
          </aside>
        )}
        {children}
      </main>
      <footer className="merchant-footer">
        Powered by <a href="https://loyalqr.sg">LoyalQR.sg</a>
      </footer>
    </div>
  );
}
